/*!
* Copyright (c) 2018, Okta, Inc. and/or its affiliates. All rights reserved.
* The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
*
* You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
* WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
*
* See the License for the specific language governing permissions and limitations under the License.
*/
<template>
    <div id="home">
        <tico-logo></tico-logo>
        <div class="about">Going somewhere? Offset the emissions your travel generates by supporting a Native American carbon project. Enter a small amount of information to make a big difference.</div>
        <form id="frontPageForm" @submit="formSubmit">
            <ul id="frontPageFormList" class="calculator-form-list">
                <li class="calculator-form-item">
                    <div class="form-icon"><i class="fas fa-map"></i></div>
                    <label for="travel" class="select-wrap">
                        <select name="travel" id="travel" v-model="formTravel" required>
                            <option disabled selected hidden value="">Method of Travel</option>
                            <option value="airplane">Airplane</option>
                            <option value="car">Vehicle</option>
                        </select>
                    </label>
                </li>
                <li class="calculator-form-item">
                    <div class="form-icon"><i :class="{fas:true, 'fa-plane-departure': this.formTravel !== 'car', 'fa-car-side': formTravel === 'car'}"></i></div>
                    <vue-google-autocomplete v-on:placechanged="saveDeparture" placeholder="City of Departure" required id="formDeparture" types="(cities)" ref="departure"></vue-google-autocomplete>
                    <div class="form-warning" v-if="departureError">You must select a location from the suggested list.</div>
                </li>
                <li class="calculator-form-item">
                    <div class="form-icon"><i :class="{fas:true, 'fa-plane-arrival': travelFormData.travel !== 'car', 'fa-car': travelFormData.travel === 'car'}"></i></div>
                    <vue-google-autocomplete v-on:placechanged="saveArrival" placeholder="City of Arrival" required id="formArrival" types="(cities)" ref="arrival"></vue-google-autocomplete>
                    <div class="form-warning" v-if="arrivalError">You must select a location from the suggested list.</div>
                </li>
                <li class="calculator-form-item">
                    <div class="form-icon"><i class="fas fa-moon"></i></div>
                    <input type="number" min="0" v-model.number="formHotel" placeholder="Number of Nights" required>
                    <div class="form-error" v-if="formHotel < 0">Cannot be a negative number</div>
                </li>
                <li class="calculator-form-item">
                    <div class="form-icon"><i class="far fa-calendar"></i></div>
                    <label for="eventSelect" class="select-wrap">
                        <select name="event" id="eventSelect" v-model="formEvent" required>
                            <option disabled selected hidden value="">Find Your Event or Organization</option>
                            <option v-if="event.active" v-for="event in eventList" :value="event">{{event.name}}</option>
                        </select>
                    </label>
                </li>
                <li id="eventInfo" v-if="this.travelFormData.event">
                    <div id="formEventDesc">{{travelFormData.event.desc}}</div>
                    <p id="eventSponsor" v-if="travelFormData.event.sponsor">Sponsored by: {{travelFormData.event.sponsor.name}}</p>
                </li>
            </ul>
            <div id="formButtonWrap"><button class="button-submit" id="frontPageSubmit"><span><span class="title">Find your footprint</span> See your estimated carbon emissions</span></button></div>
        </form>
        <ul id="eventList">
            <li class="front-page-event" v-for="event in eventList" v-if="event.active">
                <div class="event-list-trees">
                    <span class="tree-value"><i class="fas fa-tree event-list-tree"></i> {{Math.round(event.trees)}} tree{{Math.round(event.trees) === 1 ? "" : "s"}}</span> <span class="tree-subhead">of carbon offsets for</span>
                </div>
                <div class="event-list-name"><router-link :to="'/event/' + event.id">{{event.name}}</router-link></div>
            </li>
        </ul>
    </div>
</template>

<script>
    import axios from "axios";
    import Vuex from "vuex";
    import VueGoogleAutocomplete from "vue-google-autocomplete";
    import TicoLogo from "../shared/tico-logo.vue";
    export default {
        name: 'TravelCalcForm',
        components: {TicoLogo, VueGoogleAutocomplete},
        data: function() {
            return {
                claims: '',
                eventList: [],
                departureError: false,
                arrivalError: false,
                resourceServerExamples: [
                    {
                        label: 'Node/Express Resource Server Example',
                        url: 'https://github.com/okta/samples-nodejs-express-4/tree/master/resource-server'
                    },
                    {
                        label: 'Java/Spring MVC Resource Server Example',
                        url: 'https://github.com/okta/samples-java-spring-mvc/tree/master/resource-server'
                    }
                ],
                authenticated: false
            };
        },
        computed: {
            ...Vuex.mapState(["travelFormData"]),
            formTravel: {
                get() { return this.travelFormData.travel; },
                set(value) { this.$store.commit("travelFormData/setTravel", value); }
            },
            formDeparture: {
                get() { return this.travelFormData.departure; },
                set(value) { this.$store.commit("travelFormData/setDeparture", value); }
            },
            formArrival: {
                get() { return this.travelFormData.arrival; },
                set(value) { this.$store.commit("travelFormData/setArrival", value); }
            },
            formHotel: {
                get() { return this.travelFormData.hotel; },
                set(value) { this.$store.commit("travelFormData/setHotel", value); }
            },
            formEvent: {
                get() { return this.travelFormData.event; },
                set(value) { this.$store.commit("travelFormData/setEvent", value); }
            }
        },
        created() {
            this.setup();
        },
        mounted() {
            let departure = this.$store.getters["travelFormData/departure"];
            let arrival = this.$store.getters["travelFormData/arrival"];
            if (departure) {
                this.$refs.departure.update(departure.locality + ", " + departure.administrative_area_level_1 + ", " + (departure.country === "United States" ? "USA" : departure.country));
            }
            if (arrival) {
                this.$refs.arrival.update(arrival.locality + ", " + arrival.administrative_area_level_1 + ", " + (arrival.country === "United States" ? "USA" : arrival.country));
            }
        },
        methods: {
            async setup() {
                this.claims = await this.$auth.getUser();
                this.isAuthenticated();
                this.eventList = await axios.get(this.$store.getters["server"] + "/events", {headers: {"Access-Control-Allow-Origin": this.$store.getters["server"]}})
                    .then((data) => {
                        console.log(data.data.events);
                        return data.data.events;
                    });
            },
            async isAuthenticated() {
                this.authenticated = await this.$auth.isAuthenticated();
            },
            formSubmit(e) {
                let valid = true;
                e.preventDefault();
                console.log(this.formDeparture);
                console.log(this.formArrival);
                if (this.formHotel < 0) {
                    valid = false;
                }
                if (this.formDeparture === null) {
                    this.departureError = true;
                    valid = false;
                }
                if (this.formArrival === null) {
                    this.arrivalError = true;
                    valid = false;
                }
                if (valid) {
                    this.departureError = false;
                    this.arrivalError = false;
                    this.$router.push("travel/eventpay");
                }
            },
            saveDeparture(data) {
                this.formDeparture = data;
            },
            saveArrival(data) {
                this.formArrival = data;
            }
        }
    };
</script>
<style scoped lang="less">
    @import (reference) "../../assets/variables";
    sub{
        font-size: 0.7em;
    }
    #frontPageFormList{
        /*padding: 0;*/
        /*list-style: none;*/
        /*margin-bottom: 2em;*/
    }
    .front-page-form-item{
        /*display: flex;
        align-items: center;
        margin: 1em 0;
        padding: 0.5em 0 0;
        flex-wrap: wrap;
        border-bottom: 2px solid @lightest-gray;
        input, select{
            width: auto;
            flex: 1;
        }*/
    }
    .form-icon{
        /*font-size: 1.4rem;
        min-width: 30px;
        max-width: 30px;
        text-align: center;
        color: @gray;
        margin-right: 0.5em;*/
    }
    .form-warning{
        box-sizing: border-box;
        padding-left: 105px;
        width: 100%;
        font-size: 1rem;
        color: @red;
    }
    #formButtonWrap{
        text-align: center;
    }
    #eventInfo{
        display: block;
        margin-left: 40px;
        text-align: left;
        h3{
            font-size: 1rem;
            margin: 0.5rem 0;
        }
    }
    #frontPageSubmit{
        margin: 0 auto;
    }
    #eventList{
        list-style: none;
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        padding: 0;
        margin: 2em auto 0;
        text-align: left;
        min-width: 345px;
    }
    .front-page-event{
        margin: 0 0 1.5rem 0;
        grid-column: 2 / span 1;
    }
    .event-list-trees{
        display: flex;
        justify-content: left;
        align-items: center;
    }
    .tree-value{
        color: @green;
        font-size: 1rem;
        margin-right: 0.25em;
    }
    .tree-subhead{
        font-size: 0.8rem;
    }
    .event-list-name{
        font-size: 1.2rem;
        margin: 0.2em 0 0.6em 1.25rem;
    }
</style>



// WEBPACK FOOTER //
// src/components/travel-calculator/TravelCalcForm.vue